import { useCallback } from "react";
import { IconProps, toast, type Id } from "react-toastify";
import { CircleXIcon } from "lucide-react";
import { BsFillExclamationCircleFill as ExclamationIcon } from "react-icons/bs";

import { ToastOrion, ToastOrionProps } from "./ToastOrion";

type ToastOptions = {
  title?: string;
  description?: string;
  actionButton?: ToastOrionProps["actionButton"];
  type?: "success" | "error" | "info" | "warning";
  variant?: "primary" | "secondary";
  toastId?: Id;
  autoClose?: number | false | undefined;
};

type ToastSimplifiedOptions = Omit<ToastOptions, "title" | "type">;

type ToastUpdateOptions = ToastOptions & {
  autoClose?: number | false | undefined;
  isLoading?: boolean;
};

export const useToast = () => {
  const showToast = useCallback(
    ({
      title,
      description,
      actionButton,
      type = "success",
      variant = "primary",
      toastId,
      autoClose,
    }: ToastOptions = {}) => {
      let customIcon: React.ReactElement<IconProps> | undefined = undefined;
      let theme = variant === "primary" ? "light" : "colored";

      if (type === "error") {
        customIcon = <CircleXIcon size={24} strokeWidth="1.5px" style={{ marginTop: "-1px" }} />;
      }

      if (type === "warning") {
        customIcon = <ExclamationIcon size={24} style={{ marginTop: "-1px" }} />;
      }

      if (type !== "success" && type !== "error") {
        theme = "light";
      }

      const defaultAutoClose = type === "success" ? 3000 : 5000;
      const autoCloseComputed = autoClose ?? defaultAutoClose;

      return toast(
        <ToastOrion
          actionButton={actionButton}
          description={description}
          title={title ?? ""}
          type={type}
        />,
        {
          type: type,
          theme,
          position: "top-right",
          autoClose: autoCloseComputed,
          icon: customIcon,
          closeOnClick: true,
          ...(toastId && { toastId }),
        },
      );
    },
    [],
  );

  const info = useCallback(
    (title: string, options: ToastSimplifiedOptions = {}) => {
      return showToast({ ...options, title, type: "info" });
    },
    [showToast],
  );
  const success = useCallback(
    (title: string, options: ToastSimplifiedOptions = {}) => {
      return showToast({ ...options, title, type: "success" });
    },
    [showToast],
  );
  const error = useCallback(
    (title: string, options: ToastSimplifiedOptions = {}) => {
      return showToast({ ...options, title, type: "error" });
    },
    [showToast],
  );
  const warning = useCallback(
    (title: string, options: ToastSimplifiedOptions = {}) => {
      return showToast({ ...options, title, type: "warning" });
    },
    [showToast],
  );

  const loading = useCallback(
    (
      title: string,
      { description, actionButton, toastId, autoClose }: ToastSimplifiedOptions = {},
    ) => {
      return toast.loading(
        <ToastOrion
          actionButton={actionButton}
          description={description}
          title={title}
          type="loading"
        />,
        {
          position: "top-right",
          closeOnClick: false,
          ...(toastId && { toastId }),
          ...(autoClose !== undefined && { autoClose }),
        },
      );
    },
    [],
  );

  const update = useCallback(
    (
      id: Id,
      {
        type,
        title,
        description,
        actionButton,
        isLoading = false,
        autoClose = 3000,
      }: ToastUpdateOptions,
    ) => {
      return toast.update(id, {
        render: (
          <ToastOrion
            actionButton={actionButton}
            description={description}
            title={title ?? ""}
            type={type}
          />
        ),
        type,
        position: "top-right",
        autoClose,
        closeOnClick: true,
        isLoading,
      });
    },
    [],
  );

  const dismiss = useCallback((toastId?: Id) => {
    return toast.dismiss(toastId);
  }, []);

  const isActive = useCallback((toastId: Id) => {
    return toast.isActive(toastId);
  }, []);

  return {
    toast: {
      info,
      success,
      warning,
      error,
      loading,
      update,
      dismiss,
      isActive,
      show: showToast,
    },
  };
};
